import type { TeacherIdentity } from '../../domain/identity';
import type { BackupData, BackupSchema } from '../../domain/types';

export type TeacherSectionId =
  | 'accounts'
  | 'school'
  | 'products'
  | 'dosages'
  | 'containers'
  | 'cups'
  | 'water'
  | 'recommendations'
  | 'conversions'
  | 'calibration'
  | 'stains'
  | 'appearance'
  | 'backup'
  | 'qr'
  | 'reset';

export type ResettableTeacherSection =
  'productsAndDosages' | 'equipment' | 'waterHardness' | 'guidance' | 'calibration' | 'stains';

export interface TeacherSnapshot {
  data: BackupData;
  hasPin: boolean;
  user?: TeacherIdentity;
  databaseSchemaVersion: number;
  mode?: 'local' | 'cloud';
  online?: boolean;
  readOnly?: boolean;
  authenticated?: boolean;
  revision?: number | null;
  sessionExpiresAt?: string | null;
}

export type TeacherOperationErrorCode =
  'offline' | 'conflict' | 'sessionExpired' | 'rateLimited' | 'pinManagedExternally';

export class TeacherOperationError extends Error {
  constructor(public readonly code: TeacherOperationErrorCode) {
    super(code);
    this.name = 'TeacherOperationError';
  }
}

export interface BackupPreview {
  backup: BackupSchema;
  counts: {
    products: number;
    dosages: number;
    containers: number;
    cups: number;
    stains: number;
  };
  warnings: string[];
}

/**
 * Boundary between the teacher UI and persistent storage.
 * Implementations must validate data and make save/import/reset operations atomic.
 */
export interface TeacherService {
  readonly mode?: 'local' | 'cloud';
  readonly accounts?: boolean;
  loginAccount?(username: string, password: string): Promise<void>;
  load(): Promise<TeacherSnapshot>;
  saveData(data: BackupData, expectedRevision?: number | null): Promise<void | number>;
  setupPin(pin: string): Promise<void>;
  verifyPin(pin: string): Promise<boolean>;
  changePin(currentPin: string, nextPin: string): Promise<boolean>;
  exportBackup(): Promise<BackupSchema>;
  inspectBackup(source: string): Promise<BackupPreview>;
  importBackup(source: string, expectedRevision?: number | null): Promise<void>;
  resetSection(section: ResettableTeacherSection, expectedRevision?: number | null): Promise<void>;
  factoryReset(expectedRevision?: number | null): Promise<void>;
  lock?(): Promise<void>;
}

export type TeacherDataCommit = (
  update: (current: BackupData) => BackupData,
  successMessage: string,
) => Promise<boolean>;

export interface TeacherSectionProps {
  data: BackupData;
  locale: 'de' | 'ar';
  busy: boolean;
  commit: TeacherDataCommit;
}
